import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Clock, Video, MapPin, Pencil, Copy, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { bookingService, type AppointmentType } from '@/services/bookingService';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';

interface AppointmentTypeCardProps {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  price?: string;
  priceCents?: number;
  color?: string;
  telehealthEnabled: boolean;
  inPersonEnabled: boolean;
  isActive: boolean;
  isPublic: boolean;
  onEdit: () => void;
  onSuccess: () => void;
  bookingUrl?: string;
  className?: string;
}

export const AppointmentTypeCard: React.FC<AppointmentTypeCardProps> = ({
  id,
  name,
  description,
  durationMinutes,
  price,
  priceCents,
  color,
  telehealthEnabled,
  inPersonEnabled,
  isActive,
  isPublic,
  onEdit,
  onSuccess,
  bookingUrl,
  className,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { clinic } = useClinic();
  const { toast } = useToast();
  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`;
  };
  const handleToggleAppointmentType = async () => {
    if (!clinic?.id) return;
    setIsSaving(true);
    try {
      if (isActive) {
        await bookingService.deleteAppointmentType(clinic.id, id);
        toast({
          title: 'Success',
          description: 'Appointment type disabled',
        });
      } else {
        await bookingService.updateAppointmentType(clinic.id, id, { is_active: true });
        toast({
          title: 'Success',
          description: 'Appointment type enabled',
        });
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to toggle appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update appointment type',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all hover:shadow-md',
        !isActive && 'opacity-60',
        className
      )}
    >
      {/* Color accent bar */}
      <div
        className='absolute left-0 right-0 top-0 h-1'
        style={{ backgroundColor: color || '#0D9488' }}
      />

      <CardContent className='pb-4 pt-5'>
        <div className='mb-3 flex items-start justify-between'>
          <div className='flex-1'>
            <div className='mb-1 flex items-center gap-2'>
              <h3 className='font-semibold text-slate-900'>{name}</h3>
              {!isPublic && (
                <Badge variant='secondary' className='text-xs'>
                  Private
                </Badge>
              )}
            </div>
            {description && <p className='line-clamp-2 text-sm text-slate-500'>{description}</p>}
          </div>

          <Switch
            checked={isActive}
            disabled={isSaving}
            onCheckedChange={handleToggleAppointmentType}
            className='ml-4'
          />
        </div>

        {/* Details */}
        <div className='mb-4 flex flex-wrap items-center gap-3 text-sm text-slate-600'>
          <div className='flex items-center gap-1'>
            <Clock className='h-4 w-4 text-slate-400' />
            <span>{formatDuration(durationMinutes)}</span>
          </div>

          {telehealthEnabled && (
            <div className='flex items-center gap-1'>
              <Video className='h-4 w-4 text-teal-500' />
              <span>Video</span>
            </div>
          )}

          {inPersonEnabled && (
            <div className='flex items-center gap-1'>
              <MapPin className='h-4 w-4 text-blue-500' />
              <span>In-person</span>
            </div>
          )}

          {priceCents && priceCents > 0 ? (
            <Badge className='bg-emerald-100 text-emerald-700 border-emerald-200 font-medium'>
              ${(priceCents / 100).toFixed(2)}
            </Badge>
          ) : (
            <Badge variant='outline' className='font-medium text-slate-500'>
              Free
            </Badge>
          )}
        </div>

        {/* Actions */}
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={onEdit} className='flex-1'>
            <Pencil className='mr-1 h-4 w-4' />
            Edit
          </Button>

          {bookingUrl ? (
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                // bookingUrl is now the fully-qualified per-appointment-type
                // V2 URL (clinic-slug + type-slug), so we copy it as-is.
                navigator.clipboard.writeText(bookingUrl);
                toast({ title: 'Copied!', description: 'Booking link copied to clipboard' });
              }}
              className='flex-1'
            >
              <Copy className='mr-1 h-4 w-4' /> Copy Link
            </Button>
          ) : null}

          {bookingUrl ? (
            <Button
              variant='ghost'
              size='icon'
              onClick={() => window.open(bookingUrl, '_blank')}
              className='h-8 w-8'
            >
              <ExternalLink className='h-4 w-4' />
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default AppointmentTypeCard;
